import React, { useState, useEffect, useCallback } from 'react';
import { BottomSheet } from '../../../shared/ui';
import { paymentAllocationV2Api, PaymentAllocationDetailsV2 } from '../../../api/paymentAllocationV2Api';
import { PaymentList, Payment, PaymentAllocation } from '../../../shared/ui';
import './PaymentDetailsBottomSheet.css';

interface PaymentDetailsBottomSheetProps {
  isOpen: boolean;
  paymentId: number;
  siteId: number;
  currentActivityId?: number; // ID активности, с которой пришли (для подчеркивания)
  onClose: () => void;
}

const PaymentDetailsBottomSheet: React.FC<PaymentDetailsBottomSheetProps> = ({
  isOpen,
  paymentId,
  siteId,
  currentActivityId,
  onClose
}) => {
  const [allocationsData, setAllocationsData] = useState<PaymentAllocationDetailsV2 | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Загрузка данных о платеже и распределениях
  const loadPaymentDetails = useCallback(async () => {
    if (!paymentId || !siteId) return;

    setLoading(true);
    setError(null);
    try {
      const data = await paymentAllocationV2Api.getPaymentAllocations(paymentId, siteId);
      setAllocationsData(data);
    } catch (err: any) {
      console.error('Ошибка при загрузке деталей платежа:', err);
      setError(err?.response?.data || 'Не удалось загрузить детали платежа');
    } finally {
      setLoading(false);
    }
  }, [paymentId, siteId]);

  useEffect(() => {
    if (isOpen && paymentId) {
      loadPaymentDetails();
    } else {
      setAllocationsData(null);
      setError(null);
    }
  }, [isOpen, paymentId, loadPaymentDetails]);

  const formatAmount = useCallback((amount?: number) => {
    if (amount === undefined || amount === null) return '—';
    return `${amount.toLocaleString('ru-RU')} ₽`;
  }, []);

  const formatTime = useCallback((timeString: string) => {
    return new Date(timeString).toLocaleTimeString('ru-RU', {
      hour: '2-digit',
      minute: '2-digit'
    });
  }, []);

  const formatDate = useCallback((timeString: string) => {
    const date = new Date(timeString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Сегодня';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Вчера';
    } else {
      return date.toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    }
  }, []);

  // Преобразование данных для PaymentList
  const paymentListData: Payment[] = React.useMemo(() => {
    if (!allocationsData) return [];

    // Иерархия: PaymentLine -> [ Session, ... ] + [ Contract, ... ]; Session -> [ Activity, ... ]
    const allocations: PaymentAllocation[] = [];
    const activitiesByLineAndSession = new Map<number, Map<string | number, PaymentAllocation[]>>();
    const contractsByLineId = new Map<number, PaymentAllocation[]>();
    
    // Активности: группируем по статье и по смене (sessionId или дата+объект)
    if (allocationsData.activityAllocations) {
      allocationsData.activityAllocations.forEach(allocation => {
        const activityNode: PaymentAllocation = {
          allocationId: allocation.allocationId,
          allocationType: 'activity',
          sessionTimeStart: allocation.sessionTimeStart,
          sessionTimeEnd: allocation.sessionTimeEnd,
          sessionHours: allocation.sessionHours,
          allocationAmount: allocation.allocationAmount,
          sessionSiteName: allocation.sessionSiteName,
          sessionActivityId: allocation.sessionActivityId,
          sessionActivityTypeOfWorkName: allocation.sessionActivityTypeOfWorkName,
          isCurrentActivity: currentActivityId !== undefined && allocation.sessionActivityId === currentActivityId
        };
        const lineId = allocation.paymentLineId;
        const sessionKey: string | number = allocation.sessionId != null && allocation.sessionId > 0
          ? allocation.sessionId
          : `${allocation.sessionTimeStart?.slice(0, 10) ?? ''}_${allocation.sessionSiteName ?? ''}`;
        if (!activitiesByLineAndSession.has(lineId)) {
          activitiesByLineAndSession.set(lineId, new Map());
        }
        const bySession = activitiesByLineAndSession.get(lineId)!;
        if (!bySession.has(sessionKey)) {
          bySession.set(sessionKey, []);
        }
        bySession.get(sessionKey)!.push(activityNode);
      });
    }
    
    // Контракты
    if (allocationsData.contractAllocations) {
      allocationsData.contractAllocations.forEach(allocation => {
        const childAllocation: PaymentAllocation = {
          allocationId: allocation.allocationId,
          allocationType: 'contract',
          allocationAmount: allocation.allocationAmount,
          contractTitle: allocation.contractTitle,
          contractDescription: allocation.contractDescription
        };
        const lineId = allocation.paymentLineId;
        if (!contractsByLineId.has(lineId)) {
          contractsByLineId.set(lineId, []);
        }
        contractsByLineId.get(lineId)!.push(childAllocation);
      });
    }
    
    // Статьи платежа: под каждой — смены (с суммой) + контракты; под сменой — активности
    if (allocationsData.paymentLines) {
      allocationsData.paymentLines.forEach(line => {
        const bySession = activitiesByLineAndSession.get(line.id);
        const sessionNodes: PaymentAllocation[] = [];
        if (bySession) {
          const entries = Array.from(bySession.entries());
          entries.sort(([, a], [, b]) => {
            const tA = a[0]?.sessionTimeStart;
            const tB = b[0]?.sessionTimeStart;
            if (!tA) return 1;
            if (!tB) return -1;
            return new Date(tA).getTime() - new Date(tB).getTime();
          });
          entries.forEach(([sessionKey, activityNodes], index) => {
            const sumAmount = activityNodes.reduce((s, a) => s + a.allocationAmount, 0);
            const first = activityNodes[0];
            const sessionIdNum = typeof sessionKey === 'number' ? sessionKey : -line.id * 10000 - index;
            sessionNodes.push({
              allocationId: sessionIdNum,
              allocationType: 'session',
              sessionTimeStart: first?.sessionTimeStart,
              sessionTimeEnd: activityNodes.length ? activityNodes[activityNodes.length - 1]?.sessionTimeEnd : undefined,
              sessionSiteName: first?.sessionSiteName,
              allocationAmount: sumAmount,
              childAllocations: activityNodes
            });
          });
        }
        const contractNodes = contractsByLineId.get(line.id) || [];
        const childAllocations = [...sessionNodes, ...contractNodes];
        allocations.push({
          allocationId: line.id,
          allocationType: 'paymentLine',
          paymentLineType: line.type,
          paymentLineDescription: line.description,
          allocationAmount: line.amount,
          isAllocated: line.isAllocated,
          childAllocations: childAllocations.length > 0 ? childAllocations : undefined
        });
      });
    }

    return [{
      id: allocationsData.payment.id,
      userId: allocationsData.payment.userId,
      userName: allocationsData.payment.userName,
      date: allocationsData.payment.date || '',
      amount: allocationsData.payment.amount,
      allocatedAmount: allocationsData.payment.allocatedAmount,
      remainingAmount: allocationsData.payment.remainingAmount,
      signatureAdmin: allocationsData.payment.adminSignature,
      signatureUser: allocationsData.payment.userSignature,
      allocations: allocations.length > 0 ? allocations : undefined
    }];
  }, [allocationsData, currentActivityId]);

  if (!isOpen) return null;

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title="Детали платежа"
      size="large"
      showHandle={true}
      allowSwipeToClose={true}
    >
      <div className="payment-details-sheet">
        {loading ? (
          <div className="loading-state">
            <i className="ri-loader-4-line ri-spin"></i>
            <span>Загрузка деталей платежа...</span>
          </div>
        ) : error ? (
          <div className="error-state">
            <i className="ri-error-warning-line"></i>
            <span>{error}</span>
          </div>
        ) : allocationsData ? (
          <div className="allocations-section" data-card-id="payment-details-sheet">
            <PaymentList
              payments={paymentListData}
              formatTime={formatTime}
              formatDate={formatDate}
              formatAmount={formatAmount}
              loading={false}
              loadingAllocations={{}}
              emptyState={{
                icon: 'ri-inbox-line',
                title: 'Нет распределений',
                subtitle: 'Платеж еще не был распределен'
              }}
            />
          </div>
        ) : null}
      </div>
    </BottomSheet>
  );
};

export default PaymentDetailsBottomSheet;
